import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Building2, MapPin, Phone, Users, Plus, Edit2, Trash2, Home } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { mockSites } from '@/data/mockData';
import { RoomConfigSection, type RoomConfig } from '@/components/manager/RoomConfigSection';
import { ShiftTimeFramesSettings } from '@/components/settings/ShiftTimeFramesSettings';
import { ShiftReportFieldsSettings } from '@/components/settings/ShiftReportFieldsSettings';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

type FacilityType = 'men' | 'women' | 'family';

interface Site {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  capacity: number;
  is_active: boolean;
  is_family_shelter?: boolean;
  created_at: string;
}

const getFacilityTypeLabel = (site: Site): string => {
  if (site.is_family_shelter) return 'Family';
  if (site.name?.toLowerCase().includes('women')) return 'Women';
  return 'Men';
};

const getFacilityTypeFromSite = (site: Site): FacilityType => {
  if (site.is_family_shelter) return 'family';
  if (site.name?.toLowerCase().includes('women')) return 'women';
  return 'men';
};

export default function SiteManagement({ isDemo = false }: { isDemo?: boolean }) {
  const [sites, setSites] = useState<Site[]>([]);
  const [isAddSiteOpen, setIsAddSiteOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(false);

  // Room generation helper
  const generateRoomsForSite = async (siteId: string, config: { singleRooms: number; sharedRooms: number; communalRooms: number; communalBeds: number; startingNumber: number }) => {
    const { data: existingRooms } = await supabase.from('rooms').select('id').eq('site_id', siteId);
    if (existingRooms && existingRooms.length > 0) return;

    const rooms: any[] = [];
    let roomNum = config.startingNumber;

    for (let i = 0; i < config.singleRooms; i++) {
      rooms.push({ site_id: siteId, room_number: String(roomNum), capacity: 1, current_occupancy: 0, is_occupied: false, room_type: 'single' });
      roomNum++;
    }
    for (let i = 0; i < config.sharedRooms; i++) {
      rooms.push({ site_id: siteId, room_number: `${roomNum}a`, capacity: 1, current_occupancy: 0, is_occupied: false, room_type: 'shared' });
      rooms.push({ site_id: siteId, room_number: `${roomNum}b`, capacity: 1, current_occupancy: 0, is_occupied: false, room_type: 'shared' });
      roomNum++;
    }
    for (let i = 0; i < config.communalRooms; i++) {
      rooms.push({ site_id: siteId, room_number: String(roomNum), capacity: config.communalBeds, current_occupancy: 0, is_occupied: false, room_type: 'communal' });
      roomNum++;
    }

    if (rooms.length > 0) {
      const { error } = await supabase.from('rooms').insert(rooms);
      if (error) throw error;
    }
  };

  const [siteName, setSiteName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [sitePhone, setSitePhone] = useState('');
  const [siteCapacity, setSiteCapacity] = useState(50);
  const [facilityType, setFacilityType] = useState<FacilityType>('men');
  const [roomConfig, setRoomConfig] = useState<RoomConfig>({
    singleRooms: '0',
    sharedRooms: '15',
    communalRooms: '0',
    communalBeds: '4',
    startingNumber: '101',
  });
  // Load sites
  const loadSites = async () => {
    if (isDemo) {
      setSites(mockSites as any);
      return;
    }
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('*')
        .order('name');

      if (error) throw error;
      setSites(data || []);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSites();
  }, [isDemo]);

  // Create or update site
  const handleSaveSite = async () => {
    if (!siteName) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Site name is required'
      });
      return;
    }

    setLoading(true);
    const roomTypeConfig = {
      singleRooms: parseInt(roomConfig.singleRooms) || 0,
      sharedRooms: parseInt(roomConfig.sharedRooms) || 0,
      communalRooms: parseInt(roomConfig.communalRooms) || 0,
      communalBeds: parseInt(roomConfig.communalBeds) || 4,
      startingNumber: parseInt(roomConfig.startingNumber) || 101,
    };

    try {
      if (editingSite) {
          const { error } = await supabase
            .from('sites')
            .update({
              name: siteName,
              address: siteAddress,
              phone: sitePhone,
              capacity: siteCapacity,
              is_family_shelter: facilityType === 'family'
            })
            .eq('id', editingSite.id);

          if (error) throw error;

          // Generate rooms if configured
          const totalNewRooms = roomTypeConfig.singleRooms + roomTypeConfig.sharedRooms + roomTypeConfig.communalRooms;
          if (totalNewRooms > 0) {
            await generateRoomsForSite(editingSite.id, roomTypeConfig);
          }

          toast({ title: 'Success', description: 'Site updated successfully' });
        } else {
          const { data, error } = await supabase
            .from('sites')
            .insert([{
              name: siteName,
              address: siteAddress,
              phone: sitePhone,
              capacity: siteCapacity,
              is_family_shelter: facilityType === 'family'
            }])
            .select()
            .single();

        if (error) throw error;

        // Generate rooms if configured
        const totalNewRooms = roomTypeConfig.singleRooms + roomTypeConfig.sharedRooms + roomTypeConfig.communalRooms;
        if (totalNewRooms > 0 && data) {
          await generateRoomsForSite(data.id, roomTypeConfig);
        }

        toast({ title: 'Success', description: 'Site created successfully' });
      }

        setSiteName('');
        setSiteAddress('');
        setSitePhone('');
        setSiteCapacity(50);
        setFacilityType('men');
        setRoomConfig({ singleRooms: '0', sharedRooms: '15', communalRooms: '0', communalBeds: '4', startingNumber: '101' });
        setEditingSite(null);
      setIsAddSiteOpen(false);
      loadSites();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Toggle site active status
  const handleToggleActive = async (site: Site) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('sites')
        .update({ is_active: !site.is_active })
        .eq('id', site.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Site ${!site.is_active ? 'activated' : 'deactivated'}`
      });

      loadSites();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete site
  const handleDeleteSite = async (siteId: string) => {
    if (!confirm('Are you sure you want to delete this site? This will affect all related data.')) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('sites')
        .delete()
        .eq('id', siteId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Site deleted successfully'
      });

      loadSites();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (site: Site) => {
    setEditingSite(site);
    setSiteName(site.name);
    setSiteAddress(site.address || '');
    setSitePhone(site.phone || '');
    setSiteCapacity(site.capacity);
    setFacilityType(getFacilityTypeFromSite(site));
    setRoomConfig({ singleRooms: '0', sharedRooms: '0', communalRooms: '0', communalBeds: '4', startingNumber: '101' });
    setIsAddSiteOpen(true);
  };

  return (
    <div className="space-y-6">
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Site Management</CardTitle>
            <CardDescription>Add sites, configure room types, and set capacity</CardDescription>
          </div>
          <Dialog open={isAddSiteOpen} onOpenChange={(open) => {
            setIsAddSiteOpen(open);
            if (!open) {
              setEditingSite(null);
              setSiteName('');
              setSiteAddress('');
              setSitePhone('');
              setSiteCapacity(50);
              setFacilityType('men');
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Site
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingSite ? 'Edit Site' : 'Create New Site'}</DialogTitle>
                <DialogDescription>
                  {editingSite ? 'Update site information' : 'Add a new shelter location'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Facility Type *</Label>
                  <Select value={facilityType} onValueChange={(v) => setFacilityType(v as FacilityType)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select facility type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="family">
                        <div className="flex items-center gap-2">
                          <Home className="h-4 w-4" />
                          Family Shelter
                        </div>
                      </SelectItem>
                      <SelectItem value="men">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Men's Shelter
                        </div>
                      </SelectItem>
                      <SelectItem value="women">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Women's Shelter
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="name">Site Name *</Label>
                  <Input
                    id="name"
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    placeholder="Downtown Shelter"
                  />
                </div>

                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={siteAddress}
                    onChange={(e) => setSiteAddress(e.target.value)}
                    placeholder="123 Main St, City, State"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={sitePhone}
                    onChange={(e) => setSitePhone(e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <Label htmlFor="capacity">Capacity</Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={siteCapacity}
                    onChange={(e) => setSiteCapacity(parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>

                <RoomConfigSection config={roomConfig} onChange={setRoomConfig} />

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsAddSiteOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveSite} disabled={loading}>
                    {editingSite ? 'Update' : 'Create'} Site
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Site</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Capacity</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sites.map((site) => (
              <TableRow key={site.id}>
                <TableCell>
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {site.name}
                    </p>
                    {site.address && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {site.address}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={site.is_family_shelter ? 'default' : 'secondary'}>
                    {getFacilityTypeLabel(site)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {site.phone && (
                    <p className="text-sm flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {site.phone}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {site.capacity}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={site.is_active}
                      onCheckedChange={() => handleToggleActive(site)}
                    />
                    <Badge variant={site.is_active ? 'default' : 'secondary'}>
                      {site.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(site)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteSite(site.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    <Accordion type="single" collapsible className="space-y-2">
      <AccordionItem value="shift-settings" className="border rounded-lg bg-card">
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="text-left">
            <p className="font-semibold">Shift Settings</p>
            <p className="text-sm text-muted-foreground font-normal">Time frames and report fields</p>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-4">
          <ShiftTimeFramesSettings />
          <ShiftReportFieldsSettings />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
    </div>
  );
}
